import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Text from 'antd/lib/typography/Text';
import { MoreOutlined } from '@ant-design/icons';

import { CombinedState } from 'reducers';
import { Task } from 'cvat-core-wrapper';
import ActionsMenuContainer from 'containers/actions-menu/actions-menu';
import Preview from 'components/common/preview';
import AutomaticAnnotationProgress from 'components/tasks-page/automatic-annotation-progress';
import { cancelInferenceAsync } from 'actions/models-actions';
import { plural, t } from 'utils/i18n';

interface Props {
    task: Task;
}

export function taskCountsLabel(drawings: number, references: number): string {
    return `${plural(drawings, 'drawing')} · ${plural(references, 'reference')}`;
}

// the task's first annotation job: the workspace opens there, Drawings mode
export function taskPath(task: { id: number; jobs: { id: number; type?: string }[] }): string {
    const job = task.jobs.find((item) => !item.type || item.type === 'annotation') ?? task.jobs[0];
    return job ? `/tasks/${task.id}/jobs/${job.id}` : `/tasks/${task.id}`;
}

export default function TaskRow({ task }: Props): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const deletes = useSelector((state: CombinedState) => state.tasks.activities.deletes);
    const inference = useSelector((state: CombinedState) => state.models.inferences[task.id] ?? null);
    const deleted = task.id in deletes ? deletes[task.id] : false;
    const open = (): void => history.push(taskPath(task));

    return (
        <div className={`cvat-project-task-row${deleted ? ' cvat-project-task-row-deleted' : ''}`} data-task={task.id}>
            <Preview
                task={task}
                loadingClassName='cvat-project-task-preview-loading'
                emptyPreviewClassName='cvat-project-task-preview-empty'
                previewWrapperClassName='cvat-project-task-preview'
                previewClassName='cvat-project-task-preview-image'
                onClick={open}
            />
            <div className='cvat-project-task-body'>
                <button type='button' className='cvat-project-task-title' onClick={open}>{task.name}</button>
                <Text type='secondary'>{taskCountsLabel(task.size, (task.references ?? []).length)}</Text>
                <AutomaticAnnotationProgress
                    activeInference={inference}
                    cancelAutoAnnotation={() => dispatch(cancelInferenceAsync(task.id))}
                />
            </div>
            <Dropdown
                destroyPopupOnHide
                trigger={['click']}
                overlay={(
                    <ActionsMenuContainer
                        taskInstance={task}
                        onViewAnalytics={() => history.push(`/tasks/${task.id}/analytics`)}
                    />
                )}
            >
                <Button type='text' size='small' aria-label={t('More')} icon={<MoreOutlined />} />
            </Dropdown>
        </div>
    );
}
